import { logAttributeValue } from "ctxReflector/components/L3overview/logAttributes";
import { ipv4TokenRegExp, isIpv4 } from "@shared/utils/ipv4Token";

/** The BGP NOTIFICATION a peer closed the session with, as the log words it. */
export interface PeerCease {
  code: number;
  subcode: number;
  /** Literal text of `error(…)`; no translation of our own. */
  text: string;
}

export interface PeerState {
  peer: string;
  state: string;
  /** Only when `reason` carries a received notification and its error text. */
  cease?: PeerCease;
}

// exabgp's `'type': 'state'` event, printed as a Python dict inside
// `BGP message received: {...}`. Single quotes, so it is not JSON; each piece is
// read on its own instead of parsing the dict.
const STATE_EVENT = /'type':\s*'state'/;
const PEER_ADDRESS = /'address':\s*\{[^}]*'peer':\s*'([^']+)'/;
const STATE_VALUE = /'state':\s*'([^']*)'/;
const REASON = /'reason':\s*'([^']*)'/;
// `peer reset, message (notification received (6,2)) error(Cease / Administrative Shutdown)`
const NOTIFICATION_CODES = /notification received \((\d+),\s*(\d+)\)/;
const ERROR_TEXT = /\berror\((.+)\)\s*$/;

function ceaseFromReason(reason: string): PeerCease | undefined {
  const codes = reason.match(NOTIFICATION_CODES);
  const text = reason.match(ERROR_TEXT)?.[1];
  if (!codes || !text) return undefined;
  return { code: Number(codes[1]), subcode: Number(codes[2]), text };
}

/**
 * The peer, state and, when there is one, the Cease of an exabgp `state`
 * event; null for any other line. A `reason` that does not follow the pattern
 * above gives no `cease`: nothing is guessed from free text.
 */
export function extractPeerState(message: string): PeerState | null {
  if (!STATE_EVENT.test(message)) return null;
  const peer = message.match(PEER_ADDRESS)?.[1];
  const state = message.match(STATE_VALUE)?.[1];
  if (!peer || state === undefined) return null;

  const reason = message.match(REASON)?.[1];
  const cease = reason ? ceaseFromReason(reason) : undefined;
  return cease ? { peer, state, cease } : { peer, state };
}

/** `Cease / Administrative Shutdown (6,2)`: the log's own text plus its codes. */
export function ceaseLabel({ text, code, subcode }: PeerCease): string {
  return `${text} (${code},${subcode})`;
}

/**
 * The peer a line is about: the `Neighbor` attribute of its `[…]` block, else
 * the `'peer'` of an exabgp state event. Null when it names none or the value
 * is not an IPv4 (the only kind of address the clickable IP handles).
 */
export function peerIpOf(message: string): string | null {
  const peer = logAttributeValue(message, "neighbor") ?? extractPeerState(message)?.peer;
  return peer && isIpv4(peer) ? peer : null;
}

export interface TextSegment {
  text: string;
  isIp: boolean;
}

/** `text` cut so every whole-address occurrence of `ip` is a segment of its own. */
export function splitAtIp(text: string, ip: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let from = 0;
  for (const match of Array.from(text.matchAll(ipv4TokenRegExp(ip, "g")))) {
    const start = match.index ?? 0;
    if (start > from) segments.push({ text: text.slice(from, start), isIp: false });
    segments.push({ text: match[0], isIp: true });
    from = start + match[0].length;
  }
  if (from < text.length) segments.push({ text: text.slice(from), isIp: false });
  return segments;
}
